import { memo } from 'react';
import { makeClassName } from '@siteless/util';
import { motion, useScroll, useTransform } from 'motion/react';
import { TITANIC_HEIGHT, SCENE_OVERLAP, getDrift, layers } from './config';
import {
  TitanicArtwork,
  TitanicStern,
  TerrainBg,
  TerrainMg,
  TerrainFg,
  BgGradient,
} from './artwork';
import UnderwaterParticles from './UnderwaterParticles';

export { TITANIC_HEIGHT, SCENE_OVERLAP, underwaterLight } from './config';

export let TitanicScene = memo(
  ({
    className,
    flipped,
    scrollContainerRef,
    itemStart,
    containerHeight,
  }: {
    className?: string;
    flipped?: boolean;
    scrollContainerRef: React.RefObject<HTMLElement | null>;
    itemStart: number;
    containerHeight: number;
  }) => {
    let { scrollY } = useScroll({
      container: scrollContainerRef,
    });

    let scrollStart = itemStart - containerHeight - SCENE_OVERLAP;
    let scrollEnd =
      itemStart + TITANIC_HEIGHT - SCENE_OVERLAP - containerHeight;

    let shipY = useTransform(
      scrollY,
      [scrollStart, scrollEnd],
      [0, getDrift(layers.ship.speed)],
    );
    let bgGradientY = useTransform(
      scrollY,
      [scrollStart, scrollEnd],
      [0, getDrift(layers.bgGradient.speed)],
    );
    let bgGradientOpacity = useTransform(
      scrollY,
      [scrollStart, scrollEnd],
      [0, 1],
      { ease: (t: number) => t * t * t },
    );
    let terrainBgY = useTransform(
      scrollY,
      [scrollStart, scrollEnd],
      [0, getDrift(layers.terrainBg.speed)],
    );
    let terrainMgY = useTransform(
      scrollY,
      [scrollStart, scrollEnd],
      [0, getDrift(layers.terrainMg.speed)],
    );
    let terrainFgY = useTransform(
      scrollY,
      [scrollStart, scrollEnd],
      [0, getDrift(layers.terrainFg.speed)],
    );

    return (
      <div
        className={makeClassName(
          'TitanicScene relative overflow-hidden',
          flipped && '-scale-x-100',
          className,
        )}
        style={{ height: TITANIC_HEIGHT }}
      >
        {/* bg gradient */}
        <motion.div
          className="absolute inset-x-0"
          style={{
            zIndex: layers.bgGradient.zIndex,
            top: 0,
            y: bgGradientY,
            opacity: bgGradientOpacity,
          }}
        >
          <BgGradient
            className="relative"
            style={{ width: '100%', height: 150, top: -100 }}
          />
        </motion.div>

        {/* ship */}
        <motion.div
          className="absolute"
          style={{
            zIndex: layers.ship.zIndex,
            top: 0,
            left: 0,
            rotate: flipped ? 14 : -4,
            y: shipY,
          }}
        >
          <TitanicArtwork
            style={{ width: 210, position: 'relative', top: -20 }}
            className={makeClassName('w-full', flipped && '-scale-x-100')}
          />
        </motion.div>

        {/* particles */}
        <motion.div
          className="absolute inset-0"
          style={{ zIndex: 4, y: shipY }}
        >
          <UnderwaterParticles
            className="relative"
            count={40}
            style={{ top: -20 }}
          />
        </motion.div>

        {/* terrain bg */}
        <motion.div
          className="absolute inset-x-0"
          style={{
            zIndex: layers.terrainBg.zIndex,
            top: 0,
            rotate: 3,
            y: terrainBgY,
          }}
        >
          <TerrainBg
            className="relative"
            style={{ width: 856, left: -74, top: 4 }}
          />
        </motion.div>

        {/* stern */}
        <motion.div
          className="absolute inset-x-0"
          style={{
            zIndex: layers.terrainBg.zIndex,
            top: 0,
            rotate: 1,
            y: terrainBgY,
          }}
        >
          <TitanicStern
            className="relative"
            style={{ width: 80, left: 196, top: -5 }}
          />
        </motion.div>

        {/* terrain mg */}
        <motion.div
          className="absolute inset-x-0"
          style={{
            zIndex: layers.terrainMg.zIndex,
            top: 0,
            y: terrainMgY,
            rotate: 4,
          }}
        >
          <TerrainMg
            className="relative"
            style={{ width: 740, top: 22, left: -30 }}
          />
        </motion.div>

        {/* terrain fg */}
        <motion.div
          className="absolute inset-x-0"
          style={{
            zIndex: layers.terrainFg.zIndex,
            top: 0,
            y: terrainFgY,
            rotate: -8,
          }}
        >
          <TerrainFg
            className="relative"
            style={{ top: 102, left: -180, width: 798 }}
            preserveAspectRatio="xMidYMin slice"
          />
        </motion.div>
      </div>
    );
  },
);
